import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Paper, 
  Typography, 
  Chip, 
  Collapse, 
  IconButton,
  Tooltip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { 
  ExpandMore as ExpandMoreIcon, 
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';
import { v4 as uuidv4 } from 'uuid';

const useStyles = makeStyles((theme) => ({
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  treeContainer: {
    width: '100%',
    backgroundColor: 'var(--ikaro-bg-surface)',
    borderRadius: 4,
    padding: theme.spacing(2),
    overflowY: 'auto',
    overflowX: 'auto',
    flex: 1,
    boxSizing: 'border-box',
    position: 'relative',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
  treeContent: {
    minWidth: 'fit-content',
    maxWidth: 'max-content',
    paddingBottom: theme.spacing(2),
  },
  nodeContainer: {
    marginBottom: theme.spacing(0.5),
    position: 'relative',
    width: 'fit-content',
    minWidth: 'min-content',
    maxWidth: 'max-content',
  },
  nodeContent: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 4,
    backgroundColor: 'var(--ikaro-gray-light)',
    border: '1px solid var(--ikaro-gray-medium)',
    boxShadow: 'var(--ikaro-shadow-light)',
    transition: 'all 0.2s',
    position: 'relative',
    width: 'fit-content',
    minWidth: 'min-content',
    maxWidth: 'max-content',
    flexWrap: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--ikaro-gray-medium)',
      borderColor: 'var(--ikaro-orange-primary)',
    },
  },
  expandButton: {
    padding: 4,
    marginRight: theme.spacing(1),
  },
  nodeTitle: {
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    fontWeight: 500,
  },
  infoIcon: {
    fontSize: '1rem',
    color: 'var(--ikaro-gray-dark)',
    marginLeft: theme.spacing(0.5),
    opacity: 0.6,
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 1,
      color: 'var(--ikaro-orange-primary)',
    },
  },
  chip: {
    margin: 0,
    height: 22,
    fontSize: '0.7rem',
    flexShrink: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  typeChip: {
    backgroundColor: 'var(--ikaro-status-running-bg)',
  },
  listChip: {
    backgroundColor: 'var(--ikaro-purple-light)',
  },
  possibleValuesChip: {
    backgroundColor: 'var(--ikaro-orange-light)',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    flexGrow: 0,
    justifyContent: 'flex-start',
    overflow: 'hidden',
    flexShrink: 1,
    width: 'fit-content',
  },
  childrenContainer: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(0.5),
    borderLeft: `1px dashed ${theme.palette.grey[300]}`,
    marginLeft: theme.spacing(2),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: theme.spacing(2),
      height: 1,
      borderTop: `1px dashed ${theme.palette.grey[300]}`,
    },
  },
  // Estilos do modal de detalhes
  detailsDialogPaper: {
    maxWidth: '500px',
    width: '100%',
  },
  detailsDialogTitle: {
    padding: theme.spacing(1.5, 2),
    background: 'var(--ikaro-gradient-banner)',
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: 600,
      color: 'var(--ikaro-text-white)',
    },
  },
  detailsContent: {
    padding: theme.spacing(2),
  },
  detailSection: {
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ikaro-gray-dark)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: '0.95rem',
    color: 'var(--ikaro-gray-darker)',
    backgroundColor: 'var(--ikaro-gray-light)',
    padding: theme.spacing(1, 1.5),
    borderRadius: 4,
    border: '1px solid var(--ikaro-gray-medium)',
  },
  detailValueEmpty: {
    fontStyle: 'italic',
    color: 'var(--ikaro-gray-dark)',
  },
  detailChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  possibleValuesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    backgroundColor: 'var(--ikaro-gray-light)',
    padding: theme.spacing(1, 1.5),
    borderRadius: 4,
    border: '1px solid var(--ikaro-gray-medium)',
  },
  possibleValueItem: {
    backgroundColor: 'var(--ikaro-orange-light)',
    color: 'var(--ikaro-gray-darker)',
    fontSize: '0.8rem',
  },
  booleanIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  booleanYes: {
    backgroundColor: 'var(--ikaro-status-success-bg)',
    color: 'var(--ikaro-status-success-text)',
  },
  booleanNo: {
    backgroundColor: 'var(--ikaro-gray-light)',
    color: 'var(--ikaro-gray-dark)',
  },
  detailsDialogActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: 'var(--ikaro-bg-surface-secondary)',
    borderTop: '1px solid var(--ikaro-border-default)',
  },
}));

// Função para mapear tipos para labels
const getTypeLabel = (type, t) => {
  const typeLabels = {
    string: t('common:dataTypes.string'),
    int: t('common:dataTypes.int'),
    float: t('common:dataTypes.float'),
    date: t('common:dataTypes.date'),
    time: t('common:dataTypes.time'),
    date_time: t('common:dataTypes.dateTime'),
    bool: t('common:dataTypes.bool'),
    possible_values: t('common:dataTypes.possibleValues'),
  };
  return typeLabels[type] || type?.toUpperCase() || 'STRING';
};

// Componente de nó readonly
const ReadOnlyTreeNode = ({ node, level, classes, t, onNodeClick }) => {
  const [expanded, setExpanded] = useState(true);
  
  const { name, type, list, possible_values } = node.nodeData || {};
  const isLeaf = node.nodeData?.isLeaf || false;
  const hasChildren = node.children && node.children.length > 0;
  
  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };
  
  const handleNodeClick = (e) => {
    e.stopPropagation();
    if (onNodeClick) {
      onNodeClick(node);
    }
  };
  
  return (
    <div className={classes.nodeContainer}>
      <Paper
        elevation={0}
        className={classes.nodeContent}
        onClick={handleNodeClick}
      >
        {hasChildren && (
          <IconButton
            className={classes.expandButton}
            size="small"
            onClick={handleToggleExpand}
            style={{ flexShrink: 0 }}
          >
            {expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        )}
        
        <Typography
          variant="body2"
          className={classes.nodeTitle}
          title={name || 'Sem nome'}
          noWrap
        >
          {name || 'Sem nome'} {!isLeaf && list && '(Lista)'}
        </Typography>
        
        <div className={classes.chipContainer}>
          {isLeaf && (
            <>
              {type && (
                <Chip
                  label={getTypeLabel(type, t)}
                  size="small"
                  className={`${classes.chip} ${classes.typeChip}`}
                />
              )}
              
              {list && (
                <Chip
                  label={t('common:list')}
                  size="small"
                  className={`${classes.chip} ${classes.listChip}`}
                />
              )}
              
              {possible_values && possible_values.length > 0 && (
                <Tooltip title={possible_values.join(', ')}>
                  <Chip
                    label={`${possible_values.length} VALORES`}
                    size="small"
                    className={`${classes.chip} ${classes.possibleValuesChip}`}
                  />
                </Tooltip>
              )}
            </>
          )}
          
          {!isLeaf && list && (
            <Chip
              label={t('common:list')}
              size="small"
              className={`${classes.chip} ${classes.listChip}`}
            />
          )}
        </div>
        
        <Tooltip title={t('templateViewer.viewDetails') || 'Ver detalhes'}>
          <InfoIcon className={classes.infoIcon} />
        </Tooltip>
      </Paper>
      
      {hasChildren && (
        <Collapse in={expanded} timeout="auto">
          <div className={classes.childrenContainer}>
            {node.children.map((child) => (
              <ReadOnlyTreeNode
                key={child.id}
                node={child}
                level={level + 1}
                classes={classes}
                t={t}
                onNodeClick={onNodeClick}
              />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  );
};

// Função para processar um nó durante a importação (reutilizada do TemplateTree)
const processNodeForImport = (key, value) => {
  const isList = Array.isArray(value);
  const realValue = isList ? value[0] : value;
  
  const isLeafNode = realValue && (
    typeof realValue === 'object' && 
    (realValue.type || realValue.label || realValue.description)
  );
  
  if (isLeafNode) {
    let nodeType = realValue.type || 'string';
    
    if ((nodeType === 'text' || nodeType === 'string') && 
        realValue.possible_values && 
        Array.isArray(realValue.possible_values) && 
        realValue.possible_values.length > 0) {
      nodeType = 'possible_values';
    }
    
    return {
      id: uuidv4(),
      nodeData: {
        name: key,
        label: realValue.label || key,
        description: realValue.description || '',
        type: nodeType,
        list: isList,
        possible_values: realValue.possible_values || [],
        isLeaf: true
      },
      children: []
    };
  }
  
  const children = [];
  
  if (typeof realValue === 'object' && realValue !== null) {
    for (const [childKey, childValue] of Object.entries(realValue)) {
      if (childKey !== 'type' && childKey !== 'label' && childKey !== 'description' && 
          childKey !== 'possible_values') {
        children.push(processNodeForImport(childKey, childValue));
      }
    }
  }
  
  return {
    id: uuidv4(),
    nodeData: {
      name: key,
      label: '',
      description: '',
      type: 'string',
      list: isList,
      possible_values: [],
      isLeaf: false
    },
    children: children
  };
};

// Função para converter JSON para estrutura de árvore
const jsonToTreeData = (jsonData) => {
  if (!jsonData || typeof jsonData !== 'object') {
    return [];
  }
  
  const newTreeData = [];
  
  for (const [key, value] of Object.entries(jsonData)) {
    newTreeData.push(processNodeForImport(key, value));
  }
  
  return newTreeData;
};

/**
 * TemplateViewer - Componente para visualização readonly de templates JSON
 * 
 * @param {Object} props
 * @param {Object} props.templateData - Os dados do template JSON a ser exibido
 * @param {string} props.height - Altura do container (default: '400px')
 */
const TemplateViewer = ({ templateData, height = '400px' }) => {
  const classes = useStyles();
  const { t } = useTranslation('editor');
  const { t: tCommon } = useTranslation('common');
  const [treeData, setTreeData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  useEffect(() => {
    if (templateData) {
      const converted = jsonToTreeData(templateData);
      setTreeData(converted);
    } else {
      setTreeData([]);
    }
  }, [templateData]);
  
  // Função para abrir detalhes de um nó
  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setDetailsOpen(true);
  };
  
  // Função para fechar modal de detalhes
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedNode(null);
  };
  
  const renderTree = (nodes) => {
    return nodes.map((node) => (
      <ReadOnlyTreeNode
        key={node.id}
        node={node}
        level={0}
        classes={classes}
        t={t}
        onNodeClick={handleNodeClick}
      />
    ));
  };
  
  // Renderiza os detalhes do nó selecionado
  const renderNodeDetails = () => {
    if (!selectedNode) return null;
    
    const { name, label, description, type, list, possible_values, isLeaf } = selectedNode.nodeData || {};
    const hasChildren = selectedNode.children && selectedNode.children.length > 0;
    
    return (
      <>
        {/* Nome do campo */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            {t('templateViewer.fieldName') || 'Nome do Campo'}
          </Typography>
          <Typography className={classes.detailValue}>
            {name || '-'}
          </Typography>
        </Box>
        
        {/* Label */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            Label
          </Typography>
          <Typography className={`${classes.detailValue} ${!label ? classes.detailValueEmpty : ''}`}>
            {label || (t('templateViewer.notDefined') || 'Não definido')}
          </Typography>
        </Box>
        
        {/* Descrição */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            {t('templateViewer.description') || 'Descrição'}
          </Typography>
          <Typography className={`${classes.detailValue} ${!description ? classes.detailValueEmpty : ''}`}>
            {description || (t('templateViewer.notDefined') || 'Não definido')}
          </Typography>
        </Box>
        
        {/* Tipo de dado - apenas para nós folha */}
        {isLeaf && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailLabel}>
              {t('templateViewer.dataType') || 'Tipo de Dado'}
            </Typography>
            <Box className={classes.detailValue}>
              <Chip 
                label={getTypeLabel(type, t)} 
                size="small"
                className={`${classes.chip} ${classes.typeChip}`}
              />
            </Box>
          </Box>
        )}
        
        {/* É lista? */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            {t('templateViewer.isList') || 'É uma lista?'}
          </Typography>
          <Box>
            <span className={`${classes.booleanIndicator} ${list ? classes.booleanYes : classes.booleanNo}`}>
              {list ? (tCommon('yes') || 'Sim') : (tCommon('no') || 'Não')}
            </span>
          </Box>
        </Box>
        
        {/* Tipo de nó */}
        <Box className={classes.detailSection}>
          <Typography className={classes.detailLabel}>
            {t('templateViewer.nodeType') || 'Tipo de Nó'}
          </Typography>
          <Box>
            <span className={`${classes.booleanIndicator} ${isLeaf ? classes.booleanYes : classes.booleanNo}`}>
              {isLeaf 
                ? (t('templateViewer.leafNode') || 'Campo (Folha)') 
                : (t('templateViewer.groupNode') || 'Grupo (Contém filhos)')}
            </span>
            {hasChildren && (
              <Typography variant="caption" className="ml-8" style={{ color: 'var(--ikaro-gray-dark)' }}>
                ({selectedNode.children.length} {selectedNode.children.length === 1 
                  ? (t('templateViewer.child') || 'filho') 
                  : (t('templateViewer.children') || 'filhos')})
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Valores possíveis - apenas se houver */}
        {possible_values && possible_values.length > 0 && (
          <Box className={classes.detailSection}>
            <Typography className={classes.detailLabel}>
              {t('templateViewer.possibleValues') || 'Valores Possíveis'} ({possible_values.length})
            </Typography>
            <Box className={classes.possibleValuesList}>
              {possible_values.map((value, index) => (
                <Chip
                  key={index}
                  label={value}
                  size="small"
                  className={classes.possibleValueItem}
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  };
  
  return (
    <Box className={classes.viewerWrapper} style={{ height, minHeight: '200px' }}>
      {/* Container da árvore */}
      <Box className={classes.treeContainer}>
        {treeData && treeData.length > 0 ? (
          <div className={classes.treeContent}>
            {renderTree(treeData)}
          </div>
        ) : (
          <div className={classes.emptyContainer}>
            <Typography variant="body1" gutterBottom>
              {t('noItemsCreated') || 'Nenhum item no template'}
            </Typography>
            <Typography variant="body2">
              {t('emptyTemplate') || 'O template não possui estrutura definida.'}
            </Typography>
          </div>
        )}
      </Box>
      
      {/* Modal de detalhes do campo */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.detailsDialogPaper }}
      >
        <DialogTitle className={classes.detailsDialogTitle}>
          {t('templateViewer.fieldDetails') || 'Detalhes do Campo'}
        </DialogTitle>
        <DialogContent className={classes.detailsContent}>
          {renderNodeDetails()}
        </DialogContent>
        <DialogActions className={classes.detailsDialogActions}>
          <Button onClick={handleCloseDetails} color="primary">
            {tCommon('close') || 'Fechar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateViewer;
